import React, { useEffect, useState } from 'react'
import { createBrowserRouter, Navigate, RouterProvider, useParams } from 'react-router-dom'
import { Provider } from 'react-redux'
import { Toaster } from 'sonner'
import { PublicClientApplication } from '@azure/msal-browser'
import { store } from '@/store/store'
import { useTheme } from '@/hooks/useTheme'
import { useAuthSession } from '@/hooks/useAuthSession'
import { msalConfig } from '@/config/msalConfig'
import entraAuthService from '@/services/entraAuthService'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { RootRedirect } from '@/components/auth/RootRedirect'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { RoleRequirements } from '@/utils/roleUtils'
import { PUBLIC_ROUTES, PROTECTED_ROUTES, NESTED_ROUTES } from '@/constants/routes'
import LoginPage from '@pages/LoginPage'
import AuthCallback from '@/components/auth/AuthCallback'
import DashboardPage from '@pages/DashboardPage'
import HomePage from '@pages/HomePage'
import HRAgentPage from '@pages/HRAgentPage'
import HRConversationsPage from '@pages/HRConversationsPage'
import HRDocumentsPage from '@pages/HRDocumentsPage'
import OpsAgentPage from '@pages/OpsAgentPage'
import EvaluationPage from '@pages/EvaluationPage'
import TestSetDetailPage from '@pages/TestSetDetailPage'
import EvaluationResultsPage from '@pages/EvaluationResultsPage'
import SettingsPage from '@pages/SettingsPage'
import PendingApprovalPage from '@pages/PendingApprovalPage'
import GlossaryManagementPage from '@pages/GlossaryManagementPage'
import ErrorTestPage from '@pages/ErrorTestPage'
import WorkAuthUploadPage from '@pages/WorkAuthUploadPage'
import WorkAuthDashboardPage from '@pages/WorkAuthDashboardPage'
import WorkAuthListPage from '@pages/WorkAuthListPage'
import WorkAuthDetailPage from '@pages/WorkAuthDetailPage'
import WorkAuthReviewPage from '@pages/WorkAuthReviewPage'
import WorkAuthImportPage from '@pages/WorkAuthImportPage'
import WorkAuthSmartsheetPage from '@pages/WorkAuthSmartsheetPage'
import SmartsheetAuditLogsPage from '@pages/SmartsheetAuditLogsPage'
import SmartsheetBackupsPage from '@pages/SmartsheetBackupsPage'
import IncomingEmailsPage from '@pages/IncomingEmailsPage'
import NotificationTemplatesPage from '@pages/NotificationTemplatesPage'
import WorkAuthNotificationTestingPage from '@pages/WorkAuthNotificationTestingPage'
import SidebarLayout from './layouts/SidebarLayout'

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig)

// Global flag to prevent double initialization
let msalInitialized = false

// Theme initializer component
const ThemeInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useTheme() // This will initialize and apply the theme
  return <>{children}</>
}

// MSAL initializer component
const MsalInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    const initializeMsal = async () => {
      // Prevent double initialization using global flag
      if (msalInitialized) {
        console.log('✅ MSAL already initialized, skipping')
        setIsInitialized(true)
        return
      }
      
      msalInitialized = true
      
      try {
        console.log('🔄 Initializing MSAL...')
        console.log('🔄 Environment variables:', {
          clientId: import.meta.env.VITE_AZURE_CLIENT_ID,
          tenantId: import.meta.env.VITE_AZURE_TENANT_ID,
          redirectUri: import.meta.env.VITE_AZURE_REDIRECT_URI
        })
        
        // Set the MSAL instance on the service
        entraAuthService.setMsalInstance(msalInstance)
        
        // Initialize MSAL
        await entraAuthService.initialize()
        console.log('✅ MSAL initialized successfully')
        
        // Handle any pending redirects ONCE during app initialization
        console.log('🔄 Checking for auth redirect...')
        const redirectResult = await entraAuthService.handleRedirectPromise()
        if (redirectResult) {
          console.log('✅ Found redirect result:', redirectResult.username)
          entraAuthService.setActiveAccount(redirectResult)
          // Let the AuthSessionInitializer handle the backend verification
        } else {
          console.log('ℹ️ No redirect result found')
        }
        
        setIsInitialized(true)
      } catch (error) {
        console.error('❌ MSAL initialization failed:', error)
        msalInitialized = false // Reset flag on error
        setIsInitialized(true) // Still allow app to continue
      }
    }

    initializeMsal()
  }, [])

  // Show loading while MSAL initializes
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Initializing authentication...</p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

// Auth session initializer component
const AuthSessionInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoading, isSessionInitialized } = useAuthSession() // This will handle session validation

  // Show loading screen while validating session (only if we haven't initialized yet and we're loading)
  if (!isSessionInitialized && isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

// TODO: remove with the /hr/work-auth/review/:id route below.
function LegacyReviewRedirect() {
  const { id } = useParams()
  return <Navigate to={`/hr/work-auth/${id}`} replace />
}

const router = createBrowserRouter([
  {
    path: PUBLIC_ROUTES.LOGIN,
    element: <LoginPage />,
    errorElement: (
      <ErrorBoundary>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Login Error</h1>
            <p className="text-muted-foreground mb-4">Something went wrong with the login page.</p>
            <button onClick={() => window.location.href = '/'} className="bg-primary text-primary-foreground px-4 py-2 rounded">
              Go Home
            </button>
          </div>
        </div>
      </ErrorBoundary>
    )
  },
  {
    path: '/auth/callback',
    element: <AuthCallback />
  },
  {
    path: PUBLIC_ROUTES.PENDING,
    element: (
      <ProtectedRoute allowUnassigned={true}>
        <PendingApprovalPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/work-auth/upload/:token',
    element: <WorkAuthUploadPage />
  },
  {
    path: PROTECTED_ROUTES.ROOT,
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    errorElement: (
      <ErrorBoundary>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-bold mb-4">Application Error</h1>
            <p className="text-muted-foreground mb-4">
              Something went wrong. This might be due to a missing page or a technical issue.
            </p>
            <div className="space-x-2">
              <button 
                onClick={() => window.location.href = '/'} 
                className="bg-primary text-primary-foreground px-4 py-2 rounded hover:bg-primary/90"
              >
                Go Home
              </button>
              <button 
                onClick={() => window.location.reload()} 
                className="bg-secondary text-secondary-foreground px-4 py-2 rounded hover:bg-secondary/90"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    ),
    children: [
      {
        index: true,
        element: <RootRedirect />
      },
      {
        path: NESTED_ROUTES.HR,
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.adminRoles}>
            <HRAgentPage />
          </ProtectedRoute>
        )
      },
      {
        path: NESTED_ROUTES.HR_CONVERSATIONS,
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.adminRoles}>
            <HRConversationsPage />
          </ProtectedRoute>
        )
      },
      {
        path: NESTED_ROUTES.HR_DOCUMENTS,
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.adminRoles}>
            <HRDocumentsPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'hr/work-auth/dashboard',
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.adminRoles}>
            <WorkAuthDashboardPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'hr/work-auth/list',
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.adminRoles}>
            <WorkAuthListPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'hr/work-auth/review',
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.adminRoles}>
            <WorkAuthReviewPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'hr/work-auth/import',
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.adminRoles}>
            <WorkAuthImportPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'hr/work-auth/smartsheet',
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.adminRoles}>
            <WorkAuthSmartsheetPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'hr/work-auth/smartsheet/audit-logs',
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.adminRoles}>
            <SmartsheetAuditLogsPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'hr/work-auth/backups',
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.adminRoles}>
            <SmartsheetBackupsPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'hr/work-auth/emails',
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.adminRoles}>
            <IncomingEmailsPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'hr/work-auth/:id',
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.adminRoles}>
            <WorkAuthDetailPage />
          </ProtectedRoute>
        )
      },
      // TODO: remove once stale document_submitted emails (sent before commit 6d898e8)
      // are no longer being clicked — those linked to /hr/work-auth/review/:id which doesn't exist.
      {
        path: 'hr/work-auth/review/:id',
        element: <LegacyReviewRedirect />
      },
      {
        path: NESTED_ROUTES.OPS,
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.managerRoles}>
            <OpsAgentPage />
          </ProtectedRoute>
        )
      },
      {
        path: NESTED_ROUTES.EVALUATIONS,
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.systemAdminOnly}>
            <EvaluationPage />
          </ProtectedRoute>
        )
      },
      {
        path: NESTED_ROUTES.EVALUATION_DETAIL,
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.systemAdminOnly}>
            <TestSetDetailPage />
          </ProtectedRoute>
        )
      },
      {
        path: NESTED_ROUTES.EVALUATION_RESULTS,
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.systemAdminOnly}>
            <EvaluationResultsPage />
          </ProtectedRoute>
        )
      },
      {
        path: NESTED_ROUTES.SETTINGS,
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.systemAdminOnly}>
            <SettingsPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'settings/:tab',
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.systemAdminOnly}>
            <SettingsPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'settings/notification-templates',
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.adminRoles}>
            <NotificationTemplatesPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'settings/notification-testing',
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.systemAdminOnly}>
            <WorkAuthNotificationTestingPage />
          </ProtectedRoute>
        )
      },
      {
        path: NESTED_ROUTES.GLOSSARY_MANAGEMENT,
        element: (
          <ProtectedRoute roleRequirement={RoleRequirements.systemAdminOnly}>
            <GlossaryManagementPage />
          </ProtectedRoute>
        )
      },
      {
        path: NESTED_ROUTES.ERROR_TEST,
        element: <ErrorTestPage />
      }
    ]
  }
])

function App() {
  return (
    <ErrorBoundary
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center max-w-md space-y-4">
            <h1 className="text-2xl font-bold text-foreground">Application Error</h1>
            <p className="text-muted-foreground">
              The application encountered an unexpected error. Please refresh the page or contact support if the problem persists.
            </p>
            <div className="space-x-2">
              <button 
                onClick={() => window.location.reload()} 
                className="bg-primary text-primary-foreground px-4 py-2 rounded hover:bg-primary/90"
              >
                Refresh Page
              </button>
              <button 
                onClick={() => window.location.href = '/'} 
                className="bg-secondary text-secondary-foreground px-4 py-2 rounded hover:bg-secondary/90"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      }
    >
      <Provider store={store}>
        <ThemeInitializer>
          <MsalInitializer>
            <AuthSessionInitializer>
              <Toaster 
                position="top-right"
                expand={false}
                richColors
                closeButton
              />
              <RouterProvider router={router} />
            </AuthSessionInitializer>
          </MsalInitializer>
        </ThemeInitializer>
      </Provider>
    </ErrorBoundary>
  )
}

export default App 